import { TableCell, TableRow } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/Avatar";
import type { DashboardMode } from "@/lib/dashboard-mode";
import { getRoleLabel, papeisAtribuiveis } from "./role-meta";
import type { AtendimentoEmCurso } from "@/lib/em-atendimento";
import { SeloEmAtendimento } from "@/components/shared/SeloEmAtendimento";
import { getWorkloadChips, type Workload } from "./workload-meta";
import { TeamMemberActions } from "./TeamMemberActions";
import { useAuth } from "@/lib/auth";

const selectClassName =
  "h-8 shrink-0 rounded-md border border-zinc-200 bg-white px-2 text-[12.5px] font-medium text-zinc-700 dark:border-border dark:bg-background dark:text-foreground";

type Props = {
  member: any;
  mode: DashboardMode;
  /** Atendimento em curso desse membro, se houver — selo "Em atendimento". */
  atendimento?: AtendimentoEmCurso;
  workload: Workload | undefined;
  canManage: boolean;
  viewerIsOwner: boolean;
  customRoles: any[];
  rolePending: boolean;
  suspendPending: boolean;
  reactivatePending: boolean;
  removePending: boolean;
  onOpenProfile: (membershipId: string) => void;
  onRoleChange: (membershipId: string, value: string) => void;
  onSuspend: (member: any) => void;
  onReactivate: (member: any) => void;
  onRemove: (member: any) => void;
};

/** Uma linha da tabela de Equipe — só desktop. No celular a mesma lista vira `TeamMemberCard`,
 *  porque estas seis colunas pediam 433px de rolagem lateral pra alcançar as ações. */
export function TeamMemberRow({
  member: m,
  mode,
  atendimento,
  workload,
  canManage,
  viewerIsOwner,
  customRoles,
  rolePending,
  suspendPending,
  reactivatePending,
  removePending,
  onOpenProfile,
  onRoleChange,
  onSuspend,
  onReactivate,
  onRemove,
}: Props) {
  const isOwner = m.role === "OWNER";
  const souProprietario = useAuth().user?.activeOrganization?.role === "OWNER";
  const isSuspended = m.status === "SUSPENDED";
  const roleLabel = m.customRole?.label ?? getRoleLabel(m.role, mode);
  const responsibilitiesCount = (m.assignedProcedureTypeIds ?? []).length;
  const workloadChips = getWorkloadChips(m.role, m.user.id, mode, workload);

  return (
    <TableRow>
      <TableCell className="py-2.5">
        <button
          type="button"
          onClick={() => onOpenProfile(m.id)}
          title="Ver perfil"
          className="flex min-w-0 items-center gap-2.5 rounded-md text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted"
        >
          <Avatar src={m.user.image} name={m.user.name} size="sm" />
          <div className="min-w-0">
            <p className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-zinc-900 dark:text-foreground">
              <span className="truncate">{m.user.name || "(sem nome)"}</span>
              {atendimento && <SeloEmAtendimento atendimento={atendimento} compacto />}
            </p>
            <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">{m.user.email}</p>
          </div>
        </button>
      </TableCell>

      <TableCell className="py-2.5">
        {canManage && !isOwner ? (
          viewerIsOwner ? (
            <select
              value={m.customRoleId ? `custom:${m.customRoleId}` : `role:${m.role}`}
              onChange={(e) => onRoleChange(m.id, e.target.value)}
              disabled={rolePending}
              className={selectClassName}
            >
              <optgroup label="Papéis padrão">
                {papeisAtribuiveis(mode, m.role, souProprietario).map((r) => (
                  <option key={r} value={`role:${r}`}>
                    {getRoleLabel(r, mode)}
                  </option>
                ))}
              </optgroup>
              {customRoles.length > 0 && (
                <optgroup label="Papéis customizados">
                  {customRoles.map((cr: any) => (
                    <option key={cr.id} value={`custom:${cr.id}`}>
                      {cr.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          ) : (
            <select
              value={m.role}
              onChange={(e) => onRoleChange(m.id, `role:${e.target.value}`)}
              disabled={rolePending}
              className={selectClassName}
            >
              {papeisAtribuiveis(mode, m.role, souProprietario).map((r) => (
                <option key={r} value={r}>
                  {getRoleLabel(r, mode)}
                </option>
              ))}
            </select>
          )
        ) : (
          <span
            className={cn(
              "inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider",
              isOwner ? "bg-brand-900 text-white" : "bg-zinc-100 text-zinc-700 dark:bg-muted dark:text-foreground/80",
            )}
          >
            {roleLabel}
          </span>
        )}
      </TableCell>

      <TableCell className="py-2.5">
        {responsibilitiesCount > 0 ? (
          <span className="inline-flex rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600 dark:bg-muted dark:text-muted-foreground">
            {responsibilitiesCount} procedimento{responsibilitiesCount > 1 ? "s" : ""}
          </span>
        ) : (
          <span className="text-[11px] text-zinc-400 dark:text-muted-foreground/70">Nenhum atribuído</span>
        )}
      </TableCell>

      <TableCell className="py-2.5">
        {workloadChips.length > 0 ? (
          <span className="flex flex-wrap gap-1">
            {workloadChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:bg-sky-500/10 dark:text-sky-400"
              >
                {chip.label}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-[11px] text-zinc-400 dark:text-muted-foreground/70">—</span>
        )}
      </TableCell>

      <TableCell className="py-2.5">
        <span
          className={cn(
            "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
            isSuspended
              ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
              : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
          )}
        >
          {isSuspended ? "Suspenso" : "Ativo"}
        </span>
      </TableCell>

      <TableCell className="py-2.5">
        <TeamMemberActions
          member={m}
          canManage={canManage}
          suspendPending={suspendPending}
          reactivatePending={reactivatePending}
          removePending={removePending}
          onOpenProfile={onOpenProfile}
          onSuspend={onSuspend}
          onReactivate={onReactivate}
          onRemove={onRemove}
        />
      </TableCell>
    </TableRow>
  );
}
